/**
 * OWA-998: proven lineage of this tab's own task saves (plan steps 6 to 12).
 *
 * A save always carries baseVersion: the accepted version at the root of its chain, or
 * NO_BASE_VERSION for a new task. A save may also claim parentOp: that it continues a save of
 * this tab that has not been acknowledged yet. The claim is kept only while it can be proven;
 * otherwise it is dropped and the save goes out on its root base. The worst result of an
 * unproven claim is a refusal the user resolves, never a write over a change the tab never saw,
 * because the server only ever compares an accepted version.
 *
 * - Every dispatched save gets an operation id (opId). The copy that is queued (and shown in the
 *   store) and the caller's working object both record it in `pendingOp`, so an edit cloned from
 *   either continues that save.
 * - The tab keeps an in-memory outcome per operation: enqueuing, queued, failed or accepted.
 *   It is not durable; after a reload every parent is unknown, which drops the claim.
 * - The queue database retires an acknowledged save and rebases the queue rows that continue it
 *   in one transaction (idbd.acknowledgeTask), and only then is the success reported.
 */

import type Task from '../entities/task';
import {genUUID} from './picos/uuids';
import {NO_BASE_VERSION, readBaseVersion, versionRef, type BaseVersion, type StaleConflict, type VersionRef} from './staleSave';

/** Why a save was made. A reconciling save (timer clean-up) is dropped silently when refused as stale. */
export type SaveIntent = 'reconcile';

/** Queue-only metadata on a task's queue row. Never sent to the server. */
export type TaskQueueMeta = {
    opId?: string,
    parentOp?: string | null,
    saveIntent?: SaveIntent,
    conflict?: StaleConflict | null,
    /** A merge of a held group: the queue keys of that group, retired when the merge is accepted. */
    resolves?: number[],
};

export type QueuedTask = Task & TaskQueueMeta;

type Outcome =
    | {state: 'enqueuing', settled: Promise<void>}
    | {state: 'queued'}
    | {state: 'failed'}
    | {state: 'accepted', version: VersionRef};

const outcomes = new Map<string, Outcome>();
const settle = new Map<string, () => void>();

/** How long a save waits for its parent's enqueue before treating the parent as unknown. */
export const ENQUEUE_WAIT_MS = 10_000;

/**
 * The base a working object carries: its recorded base, or no base for a task that was never
 * accepted. Null when it is unknown (an accepted task that lost its base), which the caller turns
 * into a refusal rather than a guess.
 */
export function baseOf(task: Task): BaseVersion | null {
    const base = readBaseVersion(task.baseVersion);
    if (base.kind === 'version') return base.ref;
    if (base.kind === 'none') return NO_BASE_VERSION;
    if (!task.dbAddedOn) return NO_BASE_VERSION;
    return null;
}

/** A new operation is being enqueued: a save continuing it waits for the enqueue to settle. */
function beginOperation(op: string): void {
    let resolve!: () => void;
    const settled = new Promise<void>(r => { resolve = r; });
    outcomes.set(op, {state: 'enqueuing', settled});
    settle.set(op, resolve);
}

/**
 * Called when a task save is dispatched. Returns the copy that is queued and shown, carrying a
 * new operation id, the claimed parent operation and the root base. The caller's object records
 * the operation, so its next edit continues this save.
 */
export function prepareDispatch(source: Task, intent?: SaveIntent): QueuedTask {
    const op = genUUID();
    const parentOp = source.pendingOp || null;
    let base = baseOf(source);
    if (!base) {
        console.warn(`OWA-998: task ${source.id} has no base version; the save will be refused if the task exists`);
        base = NO_BASE_VERSION;
    }

    const queued = source.clone() as QueuedTask;
    queued.baseVersion = base as Task['baseVersion'];
    queued.pendingOp = op;
    queued.opId = op;
    queued.parentOp = parentOp;
    if (intent) queued.saveIntent = intent;
    const resolves = (source as QueuedTask).resolves;
    if (resolves?.length) queued.resolves = [...resolves];

    source.baseVersion = base as Task['baseVersion'];
    source.pendingOp = op;

    beginOperation(op);
    return queued;
}

/**
 * Called when a restore from the trash is dispatched. The restore names the deleted row it was
 * shown as its base, so it is refused when that deletion is no longer the task's current
 * version (plan: Other task operations).
 */
export function prepareRestore(shown: Task): QueuedTask {
    const op = genUUID();
    const recorded = readBaseVersion(shown.baseVersion);
    const base: BaseVersion = recorded.kind === 'version' ? recorded.ref
        : shown.dbAddedOn ? versionRef(shown as Task & {addedOn: number})
        : NO_BASE_VERSION;

    const queued = shown.clone() as QueuedTask;
    queued.baseVersion = base as Task['baseVersion'];
    queued.pendingOp = op;
    queued.opId = op;
    queued.parentOp = null;
    const resolves = (shown as QueuedTask).resolves;
    if (resolves?.length) queued.resolves = [...resolves];

    beginOperation(op);
    return queued;
}

const finishEnqueue = (op: string | undefined, outcome: Outcome): void => {
    if (!op) return;
    const current = outcomes.get(op);
    // An acknowledgement can arrive before the enqueue reports (another tab sent the row)
    if (current?.state !== 'accepted') outcomes.set(op, outcome);
    settle.get(op)?.();
    settle.delete(op);
};

/** The save is in the queue. */
export const markQueued = (op: string | undefined): void => finishEnqueue(op, {state: 'queued'});

/** The save never reached the queue, or was dropped from it without being accepted. */
export const markFailed = (op: string | undefined): void => finishEnqueue(op, {state: 'failed'});

/** The server accepted the save as `version` (written, unchanged, or already committed). */
export const markAccepted = (op: string | undefined, version: VersionRef): void => {
    if (!op) return;
    outcomes.set(op, {state: 'accepted', version});
    settle.get(op)?.();
    settle.delete(op);
};

/** OWA-1433: the version the server accepted an operation as, or null while it is not known to be accepted. */
export function acceptedVersion(op: string): VersionRef | null {
    const outcome = outcomes.get(op);
    return outcome?.state === 'accepted' ? outcome.version : null;
}

const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * Resolve a save's claimed parent when it is enqueued (plan step 8): wait for a parent still being
 * enqueued; keep a queued parent; drop a failed or unknown one (the save keeps its root base,
 * which already holds the parent's intent); rebase onto an accepted one.
 */
export async function resolveParent(task: QueuedTask, waitMs = ENQUEUE_WAIT_MS): Promise<void> {
    for (;;) {
        const parent = task.parentOp;
        if (!parent) return;
        const outcome = outcomes.get(parent);
        if (!outcome || outcome.state === 'failed') {
            task.parentOp = null;
            return;
        }
        if (outcome.state === 'queued') return;
        if (outcome.state === 'accepted') {
            task.baseVersion = outcome.version as Task['baseVersion'];
            task.parentOp = null;
            return;
        }
        const waited = await Promise.race([outcome.settled.then(() => true), delay(waitMs).then(() => false)]);
        if (!waited && outcomes.get(parent)?.state === 'enqueuing') {
            task.parentOp = null;
            return;
        }
    }
}

/** Test-only: forget every outcome. */
export function __resetLineage(): void {
    outcomes.clear();
    settle.clear();
}

/** Test-only: the recorded outcome of an operation. */
export const __outcomeOf = (op: string): Outcome['state'] | undefined => outcomes.get(op)?.state;
